import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Eye, EyeOff } from 'lucide-react';
import Logo from './Logo';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_PLAN_IDS = new Set(['starter', 'professional', 'team']);

const getPendingPlan = () => {
  try {
    const pendingPlan = localStorage.getItem('aeo_pending_plan');
    return PUBLIC_PLAN_IDS.has(pendingPlan) ? pendingPlan : '';
  } catch {
    return '';
  }
};

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent
  const [resendMsg, setResendMsg] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const explicitRedirect = searchParams.get('redirect');
  const redirect = explicitRedirect || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNeedsVerification(false);
    setResendState('idle');
    setResendMsg('');
    setLoading(true);

    try {
      const userData = await login(email, password);
      // Redirect admin users to admin dashboard, others to user dashboard
      if (userData.role === 'admin') {
        navigate('/admin');
      } else {
        const pendingPlan = getPendingPlan();
        const shouldResumePlan = pendingPlan && (!explicitRedirect || redirect === '/pricing');
        navigate(shouldResumePlan ? `/pricing?plan=${pendingPlan}` : redirect);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError((typeof detail === 'string' && detail) || 'Login failed. Please try again.');
      setNeedsVerification(
        err.response?.status === 403 &&
        typeof detail === 'string' &&
        detail.startsWith('Verifica la tua email')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendState('sending');
    setResendMsg('');
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, { email });
      setResendState('sent');
      setResendMsg(`Ti abbiamo rimandato il link di verifica a ${email}. Controlla anche lo spam.`);
    } catch (err) {
      setResendState('idle');
      setResendMsg(err.response?.data?.detail || 'Invio non riuscito. Riprova tra poco.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <Logo size={48} className="mx-auto mb-3" />
          <span className="font-['Outfit'] font-semibold text-2xl tracking-tight">
            AEO Orchestra <span className="text-[#0055FF]">AI</span>
          </span>
        </Link>

        {/* Card */}
        <div className="bg-[#111111] border border-white/10 rounded-3xl p-8">
          <h1 className="font-['Outfit'] text-2xl font-medium text-center mb-2">Bentornato</h1>
          <p className="text-[#A1A1AA] text-center mb-8">Accedi al tuo account AEO Orchestra</p>

          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          {needsVerification && (
            <div className="mb-6 p-4 rounded-xl bg-[#0055FF]/10 border border-[#0055FF]/30 text-sm" data-testid="resend-verification-box">
              {resendState === 'sent' ? (
                <p className="text-[#10B981]" data-testid="resend-verification-sent">{resendMsg}</p>
              ) : (
                <>
                  <p className="text-[#A1A1AA] mb-2">Non hai ricevuto l'email di verifica?</p>
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendState === 'sending'}
                    className="text-[#0055FF] hover:text-[#3377FF] font-medium disabled:opacity-50"
                    data-testid="resend-verification-btn"
                  >
                    {resendState === 'sending' ? 'Invio in corso…' : 'Invia di nuovo il link'}
                  </button>
                  {resendMsg && <p className="text-red-400 mt-2">{resendMsg}</p>}
                  <p className="text-[#A1A1AA] mt-3 text-xs">
                    Indirizzo sbagliato in fase di registrazione?{' '}
                    <Link to="/register" className="text-[#0055FF] hover:underline">Registrati di nuovo</Link>
                    {' '}con quello corretto.
                  </p>
                </>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-sm font-medium mb-2 block">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@azienda.com"
                className="input-field"
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium mb-2 block">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-field pr-12"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-[#0055FF] hover:bg-[#3377FF] text-white rounded-full py-6 font-medium"
              data-testid="login-submit-btn"
            >
              {loading ? 'Accesso in corso...' : 'Accedi'}
            </Button>
          </form>

          <p className="mt-6 text-center text-[#A1A1AA] text-sm">
            Non hai un account?{' '}
            <Link to="/register" className="text-[#0055FF] hover:text-[#3377FF] font-medium">
              Registrati
            </Link>
          </p>

          <p className="mt-2 text-center">
            <Link to="/forgot-password" className="text-sm text-[#A1A1AA] hover:text-white">
              Password dimenticata?
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
